import {
  Alert,
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  FormController,
  Input,
  LoadingButton,
  Tile,
  TileContent,
  TileGroup,
  TileStateIndicator,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  ExportPurchaseHistory,
  PurchaseHistoryOfflineDownload,
} from '../../../api/oms/order-history';
import { ICSVModalData } from '../../../models/export-csv';
import {
  IExportCSVForm,
  IExportPurchaseHistoryRequest,
  IPHOfflineDownloadRequest,
} from '../../../models/purchase-history';
import {
  getFeatureSwitchValue,
  getWindow,
  redirectToDownloadsPage,
} from '../../../utils/general';
import {
  pushPurchaseHistoryHeaderButtonEvent,
  setExportHistoryError,
} from '../../../utils/purchase-history/analytics';
import { useCommonContext } from '../../../hooks/useContexts';

export const ExportCSVForm = ({
  closeDrawer,
  setAlertState,
  orderHistoryRequest: {
    customerAccountId = '',
    endDate,
    pageNumber,
    pageSize,
    resultsFilter = '',
    searchBy,
    searchType = 'ORDERS',
    sortBy = '',
    sortOrder = 'asc',
    startDate,
    searchValue,
    filters,
  },
  totalNumberOfOrders,
}: IExportCSVForm) => {
  const { userID } = useThdCustomer();
  const [inputError, setInputError] = useState<string>('success');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { timezone } = useCommonContext();

  const generateDefaultFileName = () => {
    const dateTime = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(new Date());

    const [date, time] = dateTime ? dateTime.split('at') : ['', ''];
    const formattedDate = date?.slice(0, -1)?.replace(/[\s,]+/g, '-');
    const formattedTime = time?.slice(1)?.replace(' ', '-').replace(':', '-');
    return `Purchase_History_${formattedDate}_${formattedTime}.csv`;
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      dataType: 'SummaryData',
      fileName: generateDefaultFileName(),
    },
  });
  const watchFileName = watch('fileName');

  useEffect(() => {
    if (errors.fileName || watchFileName === '') {
      setInputError('error');
      return;
    }
    if (inputError === 'error') {
      setInputError('');
    }
  }, [errors, watchFileName]);

  const getCompleteFileName = (fileName: string): string => {
    const endsWithCSV = fileName.includes('.csv');
    return endsWithCSV ? fileName : `${fileName}.csv`;
  };

  const parseBlob = (blobText: string, contentType: string): Blob => {
    return new Blob([blobText], { type: contentType });
  };

  const openFile = (blob: Blob, fileName: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((getWindow()?.navigator as any).msSaveBlob) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (getWindow()?.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const add = document.createElement('a');
      document.body.appendChild(add);
      add.setAttribute('style', 'display: none');
      const url = (window.URL || window.webkitURL).createObjectURL(blob);
      add.href = url;
      add.download = fileName;
      add.click();

      setTimeout(() => {
        add.remove();
      }, 100);
    }
  };

  const saveFile = (data: string, fileName: string) => {
    const blob = parseBlob(data, 'data:application/csv,');
    return openFile(blob, fileName);
  };

  const closeAlert = () => {
    if (setAlertState) setAlertState(null);
  };

  const handleSuccessfulRequest = (
    response: AxiosResponse,
    fileName: string,
  ): void => {
    setAlertState({
      alertMessage:
        'Purchase History Data successfully exported. Please check your downloads folder for the file.',
      status: 'success',
      onClose: closeAlert,
    });
    if (response.data) {
      const completeFileName = getCompleteFileName(fileName);
      saveFile(response.data, completeFileName);
    }
  };

  const enableAlertError = () => {
    setAlertState({
      alertMessage:
        "We're Sorry. We are unable to export your data. Please try again later.",
      status: 'error',
    });
  };

  const handleStandardExport = (data: ICSVModalData) => {
    const request: IExportPurchaseHistoryRequest = {
      orderHistoryRequest: {
        customerAccountId,
        endDate,
        pageNumber,
        pageSize,
        resultsFilter,
        searchBy,
        searchType,
        sortBy,
        sortOrder,
        startDate,
        searchValue,
        filters,
        includeDetails: data.dataType === 'DetailsData',
        exportType: 'csv',
        timezone,
      },
    };

    ExportPurchaseHistory(request, userID)
      .then((response) => {
        if (response.status && response.data) {
          handleSuccessfulRequest(response, data.fileName);
        }
        if (!response?.status) {
          enableAlertError();
        }
      })
      .catch((error) => {
        enableAlertError();
        setExportHistoryError(error);
      })
      .finally(() => {
        setIsLoading(false);
        setInputError('');
        reset();
        closeDrawer();
      });
  };

  const handleOfflineDownloadExport = (data: {
    dataType: string;
    fileName: string;
  }) => {
    const payload: IPHOfflineDownloadRequest = {
      orderHistoryRequest: {
        customerAccountId,
        startDate,
        endDate,
        includeDetails: data.dataType === 'DetailsData',
        searchType,
        pageSize,
        pageNumber,
        searchBy,
        sortBy,
        sortOrder,
        resultsFilter,
        searchValue,
        filters,
        fileName: data.fileName.replace('.csv', ''),
        timezone,
      },
      offlineProcessRequestType: 'PURCHASE_HISTORY_EXPORT',
      downloadType: 'CSV',
    };

    PurchaseHistoryOfflineDownload(payload, userID)
      .then((response: AxiosResponse) => {
        if (response) {
          redirectToDownloadsPage('orderHistoryRedirect');
        }
      })
      .catch(() => {
        setIsLoading(false);
        setAlertState({
          alertMessage:
            "We're Sorry. We are unable to export your data. Please try again later.",
          status: 'error',
        });
      });
  };

  const onSubmit: SubmitHandler<ICSVModalData> = async (data) => {
    setInputError('success');
    setIsLoading(true);

    pushPurchaseHistoryHeaderButtonEvent(
      `export ${data.dataType === 'DetailsData' ? 'details' : 'summary'}`,
    );

    // Gets the feature switch value for the offline download threshold else sets it to 4000 orders
    const phThreshold =
      (getFeatureSwitchValue('phOfflineDownloadThreshold') as number) || 4000;

    if (totalNumberOfOrders >= phThreshold) {
      handleOfflineDownloadExport(data);
    } else {
      handleStandardExport(data);
    }
  };

  return (
    <>
      <DrawerHeader title="Export Purchase History" onClose={closeDrawer} />
      <DrawerBody>
        <div data-testid="exportCSV" className="sui-h-full">
          <div className=" sui-mb-6">
            <Alert status="info" size="small">
              Exported data will only show settled transactions. <br />
              Returns and cancellations will not show in exported data.
            </Alert>
          </div>
          <h3 className="sui-h4-bold sui-font-bold">Select Data to Export</h3>
          <p className="sui-text-base sui-mb-6">
            You can adjust the date range of exported data by adjusting your
            Date filter on Purchase History.
          </p>
          <Controller
            control={control}
            name="dataType"
            rules={{ required: true }}
            render={({ field }) => (
              <TileGroup
                orientation="vertical"
                value={field.value}
                onChange={(
                  _event: React.ChangeEvent<HTMLInputElement>,
                  newValue: string,
                ) => {
                  if (newValue) field.onChange(newValue);
                }}
                aria-label="Select data to export"
                className="sui-flex sui-flex-col sui-gap-6"
              >
                <Tile value="SummaryData" grow aria-label="Summary Data">
                  <TileContent
                    orientation="horizontal"
                    alignItems="center"
                    grow
                  >
                    <div className="sui-flex sui-flex-col sui-gap-1 sui-w-full sui-text-left sui-p-2">
                      <span className="sui-text-lg sui-font-bold">
                        Summary Data
                      </span>
                      <span className="sui-text-base sui-text-subtle">
                        This export shows all data at an order level.
                      </span>
                    </div>
                    <TileStateIndicator position="middle" />
                  </TileContent>
                </Tile>
                <Tile value="DetailsData" grow aria-label="Details Data">
                  <TileContent
                    orientation="horizontal"
                    alignItems="center"
                    grow
                  >
                    <div className="sui-flex sui-flex-col sui-gap-1 sui-w-full sui-text-left sui-p-2">
                      <span className="sui-text-lg sui-font-bold">
                        Details Data
                      </span>
                      <span className="sui-text-base sui-text-subtle">
                        This export shows all data at a line item level.
                      </span>
                    </div>
                    <TileStateIndicator position="middle" />
                  </TileContent>
                </Tile>
              </TileGroup>
            )}
          />
          <div className="sui-mt-8 sui-mb-6">
            <h3 className="sui-h4-bold sui-font-bold">File Name</h3>
            <p className="sui-text-base">
              Your data will be exported as a csv file.
            </p>
          </div>
          <FormController fullWidth status={inputError}>
            <Input
              {...register('fileName', { required: true })}
              aria-label="File Name"
              aria-describedby="fileName-input"
              fullWidth
            />
          </FormController>
          {errors.fileName && (
            <p className="sui-text-xs sui-text-danger">
              Please enter a filename.
            </p>
          )}
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div className="ButtonGroup sui-min-w-full sui-flex sui-gap-4 sui-flex-col">
          <LoadingButton
            onClick={handleSubmit(onSubmit)}
            loading={isLoading}
            variant="primary"
            disabled={errors.fileName || watchFileName === ''}
          >
            Export
          </LoadingButton>
          <Button
            onClick={closeDrawer}
            variant="secondary"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};
